import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import StudentSidebar from '../components/StudentSidebar';
import { supabase } from '../config/supabase';

const StudentTimeTable = () => {
  const { currentUser } = useAuth();
  
  // State for timetable data
  const [timetables, setTimetables] = useState([]);
  const [filteredTimetables, setFilteredTimetables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUniversityId, setCurrentUniversityId] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [studentData, setStudentData] = useState(null);
  
  // State for filters
  const [filters, setFilters] = useState({
    search: '',
    dayOfWeek: '',
    classType: '',
    lecturer: '',
    module: '',
    semester: ''
  });
  
  // State for view options
  const [viewMode, setViewMode] = useState('list'); // 'list', 'table', 'calendar'
  const [sortBy, setSortBy] = useState('day_time'); // 'day_time', 'module', 'lecturer'
  
  // State for dropdown options
  const [lecturers, setLecturers] = useState([]);
  const [modules, setModules] = useState([]);
  
  // State for detailed view modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTimetable, setSelectedTimetable] = useState(null);

  useEffect(() => {
    if (currentUser && currentUser.role === 'student') {
      console.log('StudentTimeTable: Current user found:', currentUser);
      fetchStudentData();
    } else {
      console.log('StudentTimeTable: No current user or not a student');
      setAuthError('Please log in as a student to view timetable');
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUniversityId && studentData) {
      fetchTimetables();
      fetchLecturers();
      fetchModules();
    }
  }, [currentUniversityId, studentData]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [filters, timetables, sortBy]);

  const fetchStudentData = async () => {
    try {
      console.log('StudentTimeTable: Fetching student data for user:', currentUser.id);
      
      // Get student details including university_id and group information
      const { data: student, error: studentError } = await supabase
        .from('students')
        .select(`
          *,
          groups(*),
          degree_details(*),
          universities(*)
        `)
        .eq('id', currentUser.id)
        .single();

      if (studentError) {
        console.error('StudentTimeTable: Error fetching student data:', studentError);
        setAuthError('Failed to fetch student information');
        return;
      }

      if (!student) {
        console.error('StudentTimeTable: No student found for user ID:', currentUser.id);
        setAuthError('Student information not found');
        return;
      }

      console.log('StudentTimeTable: Student data found:', student);
      setStudentData(student);
      setCurrentUniversityId(student.university_id);
      setAuthError(null);

    } catch (error) {
      console.error('StudentTimeTable: Error in fetchStudentData:', error);
      setAuthError('Failed to load student data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLecturers = async () => {
    if (!currentUniversityId) return;

    try {
      const { data, error } = await supabase
        .from('staff')
        .select('id, name')
        .eq('university_id', currentUniversityId)
        .eq('active', true)
        .in('staff_type', ['lecturer', 'professor'])
        .order('name');

      if (error) {
        console.error('Error fetching lecturers:', error);
        return;
      }

      setLecturers(data || []);
    } catch (error) {
      console.error('Error fetching lecturers:', error);
    }
  };

  const fetchModules = async () => {
    if (!currentUniversityId || !studentData) return;

    try {
      // Get modules that the student is enrolled in
      const { data, error } = await supabase
        .from('student_module_enrollments')
        .select(`
          modules(*)
        `)
        .eq('student_id', studentData.id)
        .eq('active', true);

      if (error) {
        console.error('Error fetching student modules:', error);
        return;
      }

      const moduleData = data?.map(enrollment => enrollment.modules).filter(Boolean) || [];
      setModules(moduleData);
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const fetchTimetables = async () => {
    if (!currentUniversityId || !studentData) return;

    try {
      console.log('StudentTimeTable: Fetching timetables for student:', studentData.id);
      console.log('StudentTimeTable: Student group_id:', studentData.group_id);
      console.log('StudentTimeTable: Student university_id:', currentUniversityId);

      // Fetch timetables for the student's group and university
      const { data, error } = await supabase
        .from('timetables')
        .select(`
          *,
          modules(*),
          lecturer:staff!lecturer_id(*),
          assistant_lecturer:staff!assistant_lecturer_id(*)
        `)
        .eq('university_id', currentUniversityId)
        .eq('year', studentData.current_year)
        .eq('semester', studentData.current_semester)
        .eq('active', true)
        .contains('group_ids', [studentData.group_id]);

      if (error) {
        console.error('StudentTimeTable: Error fetching timetables:', error);
        return;
      }

      console.log('StudentTimeTable: Raw timetables data:', data);

      // Process the timetables data
      const processedTimetables = (data || []).map(timetable => {
        // Format time display - extract UTC time to match what was originally selected
        const startTime = new Date(timetable.start_time);
        const endTime = new Date(timetable.end_time);
        
        // Get UTC hours and minutes to match what was originally selected
        const startHours = startTime.getUTCHours();
        const startMinutes = startTime.getUTCMinutes();
        const endHours = endTime.getUTCHours();
        const endMinutes = endTime.getUTCMinutes();
        
        // Format as 12-hour time
        const formatTime = (hours, minutes) => {
          const period = hours >= 12 ? 'PM' : 'AM';
          const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
          return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
        };
        
        const timeDisplay = `${formatTime(startHours, startMinutes)} - ${formatTime(endHours, endMinutes)}`;

        return {
          ...timetable,
          timeDisplay
        };
      });

      console.log('StudentTimeTable: Processed timetables:', processedTimetables);
      setTimetables(processedTimetables);

    } catch (error) {
      console.error('StudentTimeTable: Error in fetchTimetables:', error);
    }
  };

  const getDayOrder = (day) => {
    const dayOrder = {
      'Monday': 1,
      'Tuesday': 2,
      'Wednesday': 3,
      'Thursday': 4,
      'Friday': 5,
      'Saturday': 6,
      'Sunday': 7
    };
    return dayOrder[day] || 8;
  };

  const applyFiltersAndSort = () => {
    let filtered = [...timetables];

    // Apply filters
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(timetable =>
        timetable.modules?.module_name?.toLowerCase().includes(searchLower) ||
        timetable.modules?.module_code?.toLowerCase().includes(searchLower) ||
        timetable.lecturer?.name?.toLowerCase().includes(searchLower) ||
        timetable.venue?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.dayOfWeek) {
      filtered = filtered.filter(timetable => timetable.day_of_week === filters.dayOfWeek);
    }

    if (filters.classType) {
      filtered = filtered.filter(timetable => timetable.class_type === filters.classType);
    }

    if (filters.lecturer) {
      filtered = filtered.filter(timetable => timetable.lecturer_id === filters.lecturer);
    }

    if (filters.module) {
      filtered = filtered.filter(timetable => timetable.module_id === filters.module);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'day_time':
          const dayCompare = getDayOrder(a.day_of_week) - getDayOrder(b.day_of_week);
          if (dayCompare !== 0) return dayCompare;
          return new Date(a.start_time) - new Date(b.start_time);
        case 'module':
          return (a.modules?.module_name || '').localeCompare(b.modules?.module_name || '');
        case 'lecturer':
          return (a.lecturer?.name || '').localeCompare(b.lecturer?.name || '');
        default:
          return 0;
      }
    });

    setFilteredTimetables(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      dayOfWeek: '',
      classType: '',
      lecturer: '',
      module: '',
      semester: ''
    });
  };

  const handleViewDetails = (timetable) => {
    setSelectedTimetable(timetable);
    setShowDetailModal(true);
  };

  const renderTimetableCard = (timetable) => (
    <div
      key={timetable.id}
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #E5E7EB',
        transition: 'all 0.3s ease',
        cursor: 'pointer'
      }}
      onClick={() => handleViewDetails(timetable)}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px'
      }}>
        <div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 4px 0',
            fontFamily: 'Outfit'
          }}>
            {timetable.modules?.module_code}
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6B7280',
            margin: 0,
            fontFamily: 'Outfit'
          }}>
            {timetable.modules?.module_name}
          </p>
        </div>
        <span style={{
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: '#FEF3F2',
          color: '#F04915',
          fontFamily: 'Outfit'
        }}>
          {timetable.class_type}
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        fontSize: '14px',
        fontFamily: 'Outfit'
      }}>
        <div>
          <strong style={{ color: '#374151' }}>Day:</strong>
          <span style={{ marginLeft: '8px', color: '#6B7280' }}>
            {timetable.day_of_week}
          </span>
        </div>
        <div>
          <strong style={{ color: '#374151' }}>Time:</strong>
          <span style={{ marginLeft: '8px', color: '#6B7280' }}>
            {timetable.timeDisplay}
          </span>
        </div>
        <div>
          <strong style={{ color: '#374151' }}>Venue:</strong>
          <span style={{ marginLeft: '8px', color: '#6B7280' }}>
            {timetable.venue}
          </span>
        </div>
        <div>
          <strong style={{ color: '#374151' }}>Lecturer:</strong>
          <span style={{ marginLeft: '8px', color: '#6B7280' }}>
            {timetable.lecturer?.name}
          </span>
        </div>
      </div>
    </div>
  );

  const renderListView = () => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '20px'
    }}>
      {filteredTimetables.map(renderTimetableCard)}
    </div>
  );

  const renderTableView = () => (
    <div style={{
      overflowX: 'auto',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
    }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        background: 'white'
      }}>
        <thead>
          <tr style={{
            background: 'linear-gradient(135deg, #F04915 0%, #FF6B35 100%)'
          }}>
            <th style={{ padding: '16px 12px', color: 'white', textAlign: 'left', fontFamily: 'Outfit', fontWeight: '600' }}>Module</th>
            <th style={{ padding: '16px 12px', color: 'white', textAlign: 'left', fontFamily: 'Outfit', fontWeight: '600' }}>Day</th>
            <th style={{ padding: '16px 12px', color: 'white', textAlign: 'left', fontFamily: 'Outfit', fontWeight: '600' }}>Time</th>
            <th style={{ padding: '16px 12px', color: 'white', textAlign: 'left', fontFamily: 'Outfit', fontWeight: '600' }}>Venue</th>
            <th style={{ padding: '16px 12px', color: 'white', textAlign: 'left', fontFamily: 'Outfit', fontWeight: '600' }}>Type</th>
            <th style={{ padding: '16px 12px', color: 'white', textAlign: 'left', fontFamily: 'Outfit', fontWeight: '600' }}>Lecturer</th>
          </tr>
        </thead>
        <tbody>
          {filteredTimetables.map((timetable) => (
            <tr
              key={timetable.id}
              style={{
                borderBottom: '1px solid #E5E7EB',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onClick={() => handleViewDetails(timetable)}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#F9FAFB';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <td style={{ padding: '16px 12px', fontFamily: 'Outfit' }}>
                <div style={{ fontWeight: '600', color: '#1F2937' }}>
                  {timetable.modules?.module_code}
                </div>
                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                  {timetable.modules?.module_name}
                </div>
              </td>
              <td style={{ padding: '16px 12px', fontFamily: 'Outfit' }}>{timetable.day_of_week}</td>
              <td style={{ padding: '16px 12px', fontFamily: 'Outfit', fontSize: '13px' }}>{timetable.timeDisplay}</td>
              <td style={{ padding: '16px 12px', fontFamily: 'Outfit' }}>{timetable.venue}</td>
              <td style={{ padding: '16px 12px' }}>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '500',
                  backgroundColor: '#FEF3F2',
                  color: '#F04915',
                  fontFamily: 'Outfit'
                }}>
                  {timetable.class_type}
                </span>
              </td>
              <td style={{ padding: '16px 12px', fontFamily: 'Outfit' }}>
                <div style={{ fontWeight: '500' }}>
                  {timetable.lecturer?.name}
                </div>
                {timetable.assistant_lecturer && (
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                    Assistant: {timetable.assistant_lecturer.name}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#F5F7FA' }}>
        <StudentSidebar />
        <div style={{ marginLeft: 290, flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ 
            textAlign: 'center',
            fontFamily: 'Outfit',
            color: '#6B7280'
          }}>
            <div style={{ 
              width: '50px', 
              height: '50px', 
              border: '4px solid #F04915', 
              borderTop: '4px solid transparent', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            Loading your timetable...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F5F7FA' }}>
      <StudentSidebar />
      
      <div style={{ marginLeft: 290, flex: 1 }}>
        <Header />
        
        <div style={{ padding: '32px 40px' }}>
          {/* Error Message */}
          {authError && (
            <div style={{
              background: '#FEE2E2',
              border: '1px solid #FECACA',
              borderRadius: 8,
              padding: 16,
              marginBottom: 24,
              color: '#991B1B',
              fontFamily: 'Outfit'
            }}>
              {authError}
            </div>
          )}

          {/* Header */}
          <div style={{ marginBottom: '32px' }}>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '700',
              color: '#1F2937',
              margin: '0 0 8px 0',
              fontFamily: 'Outfit'
            }}>
              My Timetable
            </h1>
            <p style={{
              fontSize: '16px',
              color: '#6B7280',
              margin: 0,
              fontFamily: 'Outfit'
            }}>
              View your class schedule for {studentData?.groups?.group_name || 'your group'} - Year {studentData?.current_year}, Semester {studentData?.current_semester}
            </p>
          </div>

          {/* Filters and Controls */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '32px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            {/* Search and View Controls */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto auto',
              gap: '16px',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <input
                type="text"
                placeholder="Search modules, lecturers, or venues..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                style={{
                  padding: '12px 16px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Outfit',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#F04915'}
                onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
              />

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setViewMode('list')}
                  style={{
                    padding: '8px 12px',
                    border: viewMode === 'list' ? '2px solid #F04915' : '1px solid #D1D5DB',
                    borderRadius: '6px',
                    background: viewMode === 'list' ? '#FEF3F2' : 'white',
                    color: viewMode === 'list' ? '#F04915' : '#6B7280',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontFamily: 'Outfit',
                    fontWeight: '500'
                  }}
                >
                  Cards
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  style={{
                    padding: '8px 12px',
                    border: viewMode === 'table' ? '2px solid #F04915' : '1px solid #D1D5DB',
                    borderRadius: '6px',
                    background: viewMode === 'table' ? '#FEF3F2' : 'white',
                    color: viewMode === 'table' ? '#F04915' : '#6B7280',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontFamily: 'Outfit',
                    fontWeight: '500'
                  }}
                >
                  Table
                </button>
              </div>

              <button
                onClick={clearFilters}
                style={{
                  padding: '10px 16px',
                  background: '#F3F4F6',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Outfit',
                  fontWeight: '500'
                }}
              >
                Clear Filters
              </button>
            </div>

            {/* Filter Dropdowns */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <select
                value={filters.dayOfWeek}
                onChange={(e) => handleFilterChange('dayOfWeek', e.target.value)}
                style={{
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Outfit',
                  background: 'white'
                }}
              >
                <option value="">All Days</option>
                <option value="Monday">Monday</option>
                <option value="Tuesday">Tuesday</option>
                <option value="Wednesday">Wednesday</option>
                <option value="Thursday">Thursday</option>
                <option value="Friday">Friday</option>
                <option value="Saturday">Saturday</option>
                <option value="Sunday">Sunday</option>
              </select>

              <select
                value={filters.classType}
                onChange={(e) => handleFilterChange('classType', e.target.value)}
                style={{
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Outfit',
                  background: 'white'
                }}
              >
                <option value="">All Types</option>
                <option value="Lecture">Lecture</option>
                <option value="Tutorial">Tutorial</option>
                <option value="Practical">Practical</option>
                <option value="Laboratory">Laboratory</option>
                <option value="Workshop">Workshop</option>
              </select>

              <select
                value={filters.lecturer}
                onChange={(e) => handleFilterChange('lecturer', e.target.value)}
                style={{
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Outfit',
                  background: 'white'
                }}
              >
                <option value="">All Lecturers</option>
                {lecturers.map(lecturer => (
                  <option key={lecturer.id} value={lecturer.id}>
                    {lecturer.name}
                  </option>
                ))}
              </select>

              <select
                value={filters.module}
                onChange={(e) => handleFilterChange('module', e.target.value)}
                style={{
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Outfit',
                  background: 'white'
                }}
              >
                <option value="">All Modules</option>
                {modules.map(module => (
                  <option key={module.id} value={module.id}>
                    {module.module_code} - {module.module_name}
                  </option>
                ))}
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Outfit',
                  background: 'white'
                }}
              >
                <option value="day_time">Sort by Day & Time</option>
                <option value="module">Sort by Module</option>
                <option value="lecturer">Sort by Lecturer</option>
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div style={{
            marginBottom: '24px',
            fontSize: '14px',
            color: '#6B7280',
            fontFamily: 'Outfit'
          }}>
            Showing {filteredTimetables.length} of {timetables.length} classes
          </div>

          {/* Timetable Content */}
          {filteredTimetables.length === 0 ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '60px 40px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                fontSize: '48px',
                marginBottom: '16px'
              }}>
                📅
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#374151',
                margin: '0 0 8px 0',
                fontFamily: 'Outfit'
              }}>
                No classes found
              </h3>
              <p style={{
                fontSize: '16px',
                color: '#6B7280',
                margin: 0,
                fontFamily: 'Outfit'
              }}>
                {filters.search || filters.dayOfWeek || filters.classType || filters.lecturer || filters.module
                  ? 'Try adjusting your filters to see more results.'
                  : 'No timetable entries found for your current semester.'
                }
              </p>
            </div>
          ) : (
            viewMode === 'list' ? renderListView() : renderTableView()
          )}

          {/* Detail Modal */}
          {showDetailModal && selectedTimetable && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '32px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '24px'
                }}>
                  <div>
                    <h2 style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#1F2937',
                      margin: '0 0 8px 0',
                      fontFamily: 'Outfit'
                    }}>
                      {selectedTimetable.modules?.module_code}
                    </h2>
                    <p style={{
                      fontSize: '16px',
                      color: '#6B7280',
                      margin: 0,
                      fontFamily: 'Outfit'
                    }}>
                      {selectedTimetable.modules?.module_name}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: '#6B7280',
                      padding: '4px'
                    }}
                  >
                    ×
                  </button>
                </div>

                <div style={{
                  display: 'grid',
                  gap: '16px',
                  fontSize: '16px',
                  fontFamily: 'Outfit'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong style={{ color: '#374151' }}>Day:</strong>
                    <span style={{ color: '#6B7280' }}>{selectedTimetable.day_of_week}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong style={{ color: '#374151' }}>Time:</strong>
                    <span style={{ color: '#6B7280' }}>{selectedTimetable.timeDisplay}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong style={{ color: '#374151' }}>Venue:</strong>
                    <span style={{ color: '#6B7280' }}>{selectedTimetable.venue}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong style={{ color: '#374151' }}>Class Type:</strong>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: '#FEF3F2',
                      color: '#F04915'
                    }}>
                      {selectedTimetable.class_type}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong style={{ color: '#374151' }}>Lecturer:</strong>
                    <span style={{ color: '#6B7280' }}>{selectedTimetable.lecturer?.name}</span>
                  </div>
                  {selectedTimetable.assistant_lecturer && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <strong style={{ color: '#374151' }}>Assistant:</strong>
                      <span style={{ color: '#6B7280' }}>{selectedTimetable.assistant_lecturer.name}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong style={{ color: '#374151' }}>Academic Year:</strong>
                    <span style={{ color: '#6B7280' }}>{selectedTimetable.academic_year}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong style={{ color: '#374151' }}>Year:</strong>
                    <span style={{ color: '#6B7280' }}>Year {selectedTimetable.year}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong style={{ color: '#374151' }}>Semester:</strong>
                    <span style={{ color: '#6B7280' }}>Semester {selectedTimetable.semester}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentTimeTable;
